/**
 * Admin Settings Page
 *
 * Admin-only page for managing:
 * - Company details
 * - User management (invite, delete users)
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import * as authApi from '../api/authApi';
import { CURRENCIES } from '../constants/currencies';
import * as fileApi from '../api/fileApi';
import * as apiKeyApi from '../api/apiKeyApi';
import Button from '../components/common/Button';

/**
 * AdminSettings Component
 */
function AdminSettings() {
  const { user, company, setCompany } = useAuth();

  // Company settings state
  const [companyName, setCompanyName] = useState(company?.name || '');
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyError, setCompanyError] = useState('');
  const [companySuccess, setCompanySuccess] = useState('');

  // Base currency state
  const [baseCurrency, setBaseCurrency] = useState(company?.baseCurrency || 'USD');
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [currencyError, setCurrencyError] = useState('');
  const [currencySuccess, setCurrencySuccess] = useState('');

  // Logo state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoSuccess, setLogoSuccess] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [logoSignedUrl, setLogoSignedUrl] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // New user form state
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [addingUser, setAddingUser] = useState(false);
  const [userError, setUserError] = useState('');

  // Edit user state
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', role: 'user' });
  const [editError, setEditError] = useState('');
  const [savingUser, setSavingUser] = useState(false);

  // Delete confirmation state
  const [deletingUserId, setDeletingUserId] = useState(null);

  // API Key state
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(true);
  const [showAddApiKey, setShowAddApiKey] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [revokingKeyId, setRevokingKeyId] = useState(null);

  /**
   * Fetch users, providers, and API keys on mount
   */
  useEffect(() => {
    fetchUsers();
    fetchApiKeys();
  }, []);

  /**
   * Update company name when company context changes
   */
  useEffect(() => {
    if (company?.name) {
      setCompanyName(company.name);
    }
    if (company?.baseCurrency) {
      setBaseCurrency(company.baseCurrency);
    }
  }, [company]);

  /**
   * Fetch signed URL for logo when company has logoObjectKey
   */
  useEffect(() => {
    if (!company || !company.logoObjectKey) {
      setLogoSignedUrl(null);
      return;
    }
    setLogoSignedUrl(fileApi.buildProxyUrl(company.logoObjectKey));
  }, [company]);

  /**
   * Fetch all company users
   */
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await authApi.getCompanyUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  /**
   * Fetch API keys
   */
  const fetchApiKeys = async () => {
    try {
      setLoadingApiKeys(true);
      const response = await apiKeyApi.getApiKeys();
      setApiKeys(response.data.keys || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setLoadingApiKeys(false);
    }
  };

  /**
   * Handle creating a new API key
   */
  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    setApiKeyError('');

    if (!newApiKeyName.trim()) {
      setApiKeyError('Key name is required');
      return;
    }

    try {
      setCreatingApiKey(true);
      const response = await apiKeyApi.createApiKey({
        name: newApiKeyName.trim(),
        permissions: ['upload:documents']
      });

      // Store the newly created key to show to user
      setNewlyCreatedKey(response.data.plainKey);
      setNewApiKeyName('');
      fetchApiKeys();
    } catch (error) {
      setApiKeyError(error.message || 'Failed to create API key');
    } finally {
      setCreatingApiKey(false);
    }
  };

  /**
   * Handle revoking an API key
   */
  const handleRevokeApiKey = async (keyId) => {
    try {
      setRevokingKeyId(keyId);
      await apiKeyApi.revokeApiKey(keyId);
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      alert(error.message || 'Failed to revoke API key');
    } finally {
      setRevokingKeyId(null);
    }
  };

  /**
   * Handle deleting an API key
   */
  const handleDeleteApiKey = async (keyId) => {
    if (!confirm('Are you sure you want to permanently delete this API key?')) {
      return;
    }

    try {
      setRevokingKeyId(keyId);
      await apiKeyApi.deleteApiKey(keyId);
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to delete API key:', error);
      alert(error.message || 'Failed to delete API key');
    } finally {
      setRevokingKeyId(null);
    }
  };

  /**
   * Copy text to clipboard
   */
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  /**
   * Handle company name update
   */
  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    setCompanyError('');
    setCompanySuccess('');

    if (!companyName.trim()) {
      setCompanyError('Company name is required');
      return;
    }

    try {
      setSavingCompany(true);
      const response = await authApi.updateCompany({ name: companyName.trim() });

      // Update context if setCompany is available
      if (setCompany && response.data.company) {
        setCompany(response.data.company);
      }

      setCompanySuccess('Company name updated successfully');
      setTimeout(() => setCompanySuccess(''), 3000);
    } catch (error) {
      setCompanyError(error.message || 'Failed to update company');
    } finally {
      setSavingCompany(false);
    }
  };

  /**
   * Handle base currency change
   */
  const handleUpdateCurrency = async (newCurrency) => {
    setCurrencyError('');
    setCurrencySuccess('');
    setBaseCurrency(newCurrency);

    try {
      setSavingCurrency(true);
      const response = await authApi.updateCompany({ baseCurrency: newCurrency });

      if (setCompany && response.data.company) {
        setCompany(response.data.company);
      }

      setCurrencySuccess('Base currency updated');
      setTimeout(() => setCurrencySuccess(''), 3000);
    } catch (error) {
      setCurrencyError(error.message || 'Failed to update currency');
    } finally {
      setSavingCurrency(false);
    }
  };

  /**
   * Process and upload a logo file
   */
  const processLogoFile = async (file) => {
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setLogoError('Please select a PNG or JPEG image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('Image must be less than 5MB');
      return;
    }

    setLogoError('');
    setLogoSuccess('');

    try {
      setUploadingLogo(true);
      const response = await authApi.uploadCompanyLogo(file);

      if (setCompany && response.data.company) {
        setCompany(response.data.company);
      }

      setLogoSuccess('Logo uploaded successfully');
      setTimeout(() => setLogoSuccess(''), 3000);
    } catch (error) {
      setLogoError(error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  /**
   * Handle file input change
   */
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    await processLogoFile(file);
    e.target.value = '';
  };

  /**
   * Handle drag events
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    await processLogoFile(file);
  };

  /**
   * Handle paste event for logo upload
   */
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            await processLogoFile(file);
            break;
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [setCompany]);

  /**
   * Handle logo deletion
   */
  const handleDeleteLogo = async () => {
    setLogoError('');
    setLogoSuccess('');

    try {
      setUploadingLogo(true);
      const response = await authApi.deleteCompanyLogo();

      if (setCompany && response.data.company) {
        setCompany(response.data.company);
      }

      setLogoSuccess('Logo removed successfully');
      setTimeout(() => setLogoSuccess(''), 3000);
    } catch (error) {
      setLogoError(error.message || 'Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  /**
   * Handle adding a new user
   */
  const handleAddUser = async (e) => {
    e.preventDefault();
    setUserError('');

    if (!newUser.name || !newUser.email || !newUser.password) {
      setUserError('All fields are required');
      return;
    }

    if (newUser.password.length < 8) {
      setUserError('Password must be at least 8 characters');
      return;
    }

    try {
      setAddingUser(true);
      await authApi.inviteUser(newUser);

      // Reset form and refresh users
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      setShowAddUser(false);
      fetchUsers();
    } catch (error) {
      setUserError(error.message || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  /**
   * Handle deleting a user
   */
  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUserId(userId);
      await authApi.deleteUser(userId);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  /**
   * Start editing a user
   */
  const handleStartEdit = (u) => {
    setEditingUserId(u._id);
    setEditForm({ name: u.name, email: u.email, password: '', role: u.role });
    setEditError('');
  };

  /**
   * Cancel editing
   */
  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditForm({ name: '', email: '', password: '', role: 'user' });
    setEditError('');
  };

  /**
   * Save edited user
   */
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditError('');

    if (!editForm.name || !editForm.email) {
      setEditError('Name and email are required');
      return;
    }

    if (editForm.password && editForm.password.length < 8) {
      setEditError('Password must be at least 8 characters');
      return;
    }

    const updates = {
      name: editForm.name,
      email: editForm.email,
      role: editForm.role,
    };
    if (editForm.password) {
      updates.password = editForm.password;
    }

    try {
      setSavingUser(true);
      await authApi.updateUser(editingUserId, updates);
      setEditingUserId(null);
      fetchUsers();
    } catch (error) {
      setEditError(error.message || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
        <p className="text-gray-600">Manage your company and team members</p>
      </div>

      {/* Company Logo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Company Logo</h2>
          <p className="text-sm text-gray-500">This logo will appear in the header of the application</p>
        </div>
        <div className="p-6">
          {/* Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              relative border-2 border-dashed rounded-lg p-6 transition-colors
              ${isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
              }
            `}
          >
            <div className="flex items-start space-x-6">
              {/* Current Logo Preview */}
              <div className="flex-shrink-0">
                {logoSignedUrl ? (
                  <img
                    src={logoSignedUrl}
                    alt="Company logo"
                    className="w-24 h-24 object-contain border border-gray-200 rounded-lg bg-white"
                  />
                ) : (
                  <div className="w-24 h-24 border border-gray-200 rounded-lg flex items-center justify-center bg-white">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Upload Controls */}
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      {uploadingLogo ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          Upload Logo
                        </>
                      )}
                    </span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="sr-only"
                    />
                  </label>

                  {company?.logoObjectKey && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleDeleteLogo}
                      disabled={uploadingLogo}
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <p className="mt-2 text-sm text-gray-500">
                  Drag & drop, paste, or click to upload. PNG or JPEG, max 5MB.
                </p>

              {logoError && (
                <p className="mt-2 text-sm text-red-600">{logoError}</p>
              )}
              {logoSuccess && (
                <p className="mt-2 text-sm text-green-600">{logoSuccess}</p>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Company Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Company Settings</h2>
        </div>
        <form onSubmit={handleUpdateCompany} className="p-6">
          <div className="max-w-md">
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {companyError && (
              <p className="mt-2 text-sm text-red-600">{companyError}</p>
            )}
            {companySuccess && (
              <p className="mt-2 text-sm text-green-600">{companySuccess}</p>
            )}
          </div>
          <div className="mt-4">
            <Button type="submit" loading={savingCompany}>
              Save Changes
            </Button>
          </div>

          {/* Base Currency */}
          <div className="mt-6 pt-6 border-t border-gray-200 max-w-md">
            <label htmlFor="baseCurrency" className="block text-sm font-medium text-gray-700 mb-1">
              Base Currency
            </label>
            <p className="text-xs text-gray-500 mb-2">
              PO totals will be converted to this currency on the dashboard
            </p>
            <select
              id="baseCurrency"
              value={baseCurrency}
              onChange={(e) => handleUpdateCurrency(e.target.value)}
              disabled={savingCurrency}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} - {c.name}
                </option>
              ))}
            </select>
            {savingCurrency && (
              <p className="mt-2 text-sm text-gray-500">Saving...</p>
            )}
            {currencyError && (
              <p className="mt-2 text-sm text-red-600">{currencyError}</p>
            )}
            {currencySuccess && (
              <p className="mt-2 text-sm text-green-600">{currencySuccess}</p>
            )}
          </div>
        </form>
      </div>

      {/* User Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Team Members</h2>
          <Button onClick={() => setShowAddUser(!showAddUser)}>
            {showAddUser ? 'Cancel' : '+ Add User'}
          </Button>
        </div>

        {/* Add User Form */}
        {showAddUser && (
          <form onSubmit={handleAddUser} className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="userName"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder=""
                />
              </div>
              <div>
                <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="userEmail"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder=""
                />
              </div>
              <div>
                <label htmlFor="userPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="userPassword"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder=""
                />
              </div>
              <div>
                <label htmlFor="userRole" className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  id="userRole"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            {userError && (
              <p className="mt-3 text-sm text-red-600">{userError}</p>
            )}
            <div className="mt-4">
              <Button type="submit" loading={addingUser}>
                Add User
              </Button>
            </div>
          </form>
        )}

        {/* Users List */}
        {loadingUsers ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>No team members found.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {users.map((u) => (
              editingUserId === u._id ? (
                <form key={u._id} onSubmit={handleSaveEdit} className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                      <input
                        type="password"
                        value={editForm.password}
                        onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Leave blank to keep current"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        disabled={u._id === user?.id}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                      {u._id === user?.id && (
                        <p className="mt-1 text-xs text-gray-500">You cannot change your own role</p>
                      )}
                    </div>
                  </div>
                  {editError && (
                    <p className="mt-3 text-sm text-red-600">{editError}</p>
                  )}
                  <div className="mt-4 flex items-center space-x-3">
                    <Button type="submit" loading={savingUser}>
                      Save
                    </Button>
                    <Button variant="secondary" onClick={handleCancelEdit} disabled={savingUser}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div key={u._id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium">
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {u.name}
                        {u._id === user?.id && (
                          <span className="ml-2 text-xs text-gray-500">(you)</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      u.role === 'admin'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {u.role}
                    </span>
                    <span className="text-sm text-gray-500">
                      Joined {formatDate(u.createdAt)}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleStartEdit(u)}
                    >
                      Edit
                    </Button>
                    {u._id !== user?.id && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteUser(u._id)}
                        loading={deletingUserId === u._id}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* API Keys Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-medium text-gray-900">API Keys</h2>
            <p className="text-sm text-gray-500">Manage API keys for automated document uploads</p>
          </div>
          <Button onClick={() => {
            setShowAddApiKey(!showAddApiKey);
            setNewlyCreatedKey(null);
            setApiKeyError('');
          }}>
            {showAddApiKey ? 'Cancel' : '+ Create API Key'}
          </Button>
        </div>

        {/* Newly Created Key Alert */}
        {newlyCreatedKey && (
          <div className="p-6 bg-green-50 border-b border-green-200">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-800">API Key Created Successfully</h3>
                <p className="mt-1 text-sm text-green-700">
                  Copy this key now - it will not be shown again!
                </p>
                <div className="mt-3 flex items-center space-x-2">
                  <code className="px-3 py-2 bg-white border border-green-300 rounded text-sm font-mono text-gray-800 select-all">
                    {newlyCreatedKey}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(newlyCreatedKey)}
                  >
                    Copy
                  </Button>
                </div>
                <button
                  onClick={() => setNewlyCreatedKey(null)}
                  className="mt-3 text-sm text-green-600 hover:text-green-800"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create API Key Form */}
        {showAddApiKey && !newlyCreatedKey && (
          <form onSubmit={handleCreateApiKey} className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="max-w-md">
              <label htmlFor="apiKeyName" className="block text-sm font-medium text-gray-700 mb-1">
                Key Name
              </label>
              <input
                type="text"
                id="apiKeyName"
                value={newApiKeyName}
                onChange={(e) => setNewApiKeyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder=""
              />
              <p className="mt-1 text-xs text-gray-500">
                Give your key a descriptive name to identify its purpose
              </p>
            </div>
            {apiKeyError && (
              <p className="mt-3 text-sm text-red-600">{apiKeyError}</p>
            )}
            <div className="mt-4">
              <Button type="submit" loading={creatingApiKey}>
                Create API Key
              </Button>
            </div>
          </form>
        )}

        {/* API Keys List */}
        {loadingApiKeys ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <p>No API keys created yet.</p>
            <p className="text-sm mt-1">Create an API key to enable automated document uploads.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {apiKeys.map((key) => (
              <div key={key._id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    key.isActive ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <svg className={`w-5 h-5 ${key.isActive ? 'text-green-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {key.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      <code className="text-xs bg-gray-100 px-1 rounded">{key.prefix}...</code>
                      {' '}
                      {key.lastUsedAt ? (
                        <>Last used {formatDate(key.lastUsedAt)}</>
                      ) : (
                        <>Never used</>
                      )}
                      {key.usageCount > 0 && (
                        <> ({key.usageCount} requests)</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    key.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {key.isActive ? 'Active' : 'Revoked'}
                  </span>
                  <span className="text-sm text-gray-500">
                    Created {formatDate(key.createdAt)}
                  </span>
                  {key.isActive ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRevokeApiKey(key._id)}
                      loading={revokingKeyId === key._id}
                    >
                      Revoke
                    </Button>
                  ) : (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteApiKey(key._id)}
                      loading={revokingKeyId === key._id}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* API Usage Instructions */}
        {apiKeys.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">How to use</h4>
            <p className="text-xs text-gray-500 mb-2">
              Send documents to the upload endpoint with your API key:
            </p>
            <code className="block text-xs bg-gray-100 p-3 rounded overflow-x-auto">
              curl -X POST {window.location.origin}/api/ingest/upload \<br />
              &nbsp;&nbsp;-H "X-API-Key: your_api_key" \<br />
              &nbsp;&nbsp;-F "file=@document.pdf"
            </code>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSettings;
